/**
 * Picks a name not in `takenNames` by adding a two-digit suffix before the extension:
 * `image.png` → `image_01.png` → `image_02.png`. `takenNames` must hold lower-cased names.
 */
export function getUniqueFileName(name: string, takenNames: ReadonlySet<string>): string {
  if (!takenNames.has(name.toLowerCase())) return name;

  // A leading dot (`.env`) is part of the name, not an extension.
  const dotIndex = name.lastIndexOf('.');
  const base = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const extension = dotIndex > 0 ? name.slice(dotIndex) : '';

  for (let suffix = 1; ; suffix += 1) {
    const candidate = `${base}_${String(suffix).padStart(2, '0')}${extension}`;
    if (!takenNames.has(candidate.toLowerCase())) return candidate;
  }
}

/**
 * Renames files whose name is already taken (`image.png` → `image_01.png`), including earlier files
 * in the same batch. Case-insensitive.
 */
export function renameDuplicateFiles(files: File[], existingNames: Iterable<string>): File[] {
  const takenNames = new Set(Array.from(existingNames, (name) => name.toLowerCase()));

  return files.map((file) => {
    const name = getUniqueFileName(file.name, takenNames);
    takenNames.add(name.toLowerCase());
    return name === file.name ? file : new File([file], name, { type: file.type, lastModified: file.lastModified });
  });
}
